import * as React from 'react';
import { useFormContext, FieldError } from 'react-hook-form';
import { CalendarIcon } from 'lucide-react';
import { es } from 'date-fns/locale';
import { Input } from '../ui/input';
import { Calendar } from '../ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import type { Form } from '../../interfaces/form.interface';
import type { Metadata } from '../../interfaces/metadata.interface';
import { BaseField } from './BaseField';

interface DateFieldProps {
  meta: Metadata;
  index: number;
  configuration: Form;
}

function formatDate(date: Date | undefined) {
  if (!date) {
    return "";
  }

  return date.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function isValidDate(date: Date | undefined) {
  if (!date) {
    return false;
  }
  return !isNaN(date.getTime());
}

export const DateField: React.FC<DateFieldProps> = ({
  meta,
  index,
  configuration
}) => {
  const { register, formState: { errors }, setValue } = useFormContext();
  const error = errors[meta.id] as FieldError | undefined;

  const [open, setOpen] = React.useState(false);
  const [date, setDate] = React.useState<Date | undefined>(
    meta.default ? new Date(meta.default as string) : undefined
  );
  const [month, setMonth] = React.useState<Date | undefined>(date || new Date());
  const [value, setValueState] = React.useState(formatDate(date));

  return (
    <BaseField
      meta={meta}
      index={index}
      error={error}
      configuration={configuration}
    >
      <div className="relative">
        <Popover open={open} onOpenChange={setOpen}>
          <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none z-10" />
          <Input
            id={meta.id}
            value={value}
            placeholder="Selecciona fecha"
            className={`pl-10 ${error ? 'border-red-500' : ''}`}
            {...register(meta.id)}
            onChange={(e) => {
              const inputValue = e.target.value;
              const parsedDate = new Date(inputValue);
              setValueState(inputValue);
              if (isValidDate(parsedDate)) {
                setDate(parsedDate);
                setMonth(parsedDate);
                setValue(meta.id, parsedDate.toISOString());
              }
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setOpen(true);
              }
            }}
          />
          <PopoverTrigger asChild>
            <button
              type="button"
              className="absolute inset-0 opacity-0 cursor-pointer"
              aria-label="Open calendar"
            />
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0"
            align="start"
            sideOffset={10}
          >
            <Calendar
              mode="single"
              selected={date}
              month={month}
              captionLayout='dropdown'
              locale={es}
              onMonthChange={setMonth}
              onSelect={(selectedDate) => {
                setDate(selectedDate);
                setValueState(formatDate(selectedDate));
                setValue(meta.id, selectedDate?.toISOString() || '');
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
    </BaseField>
  );
};
